import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  NgDialogModule,
  PipesModule,
  TypedFormControl,
  TypedFormGroup,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import { Patient, Recalls } from '@principle-theorem/principle-core';
import {
  BrandRecallField,
  DEFAULT_RECALL_PERIOD_IN_MONTHS,
  IAppointment,
  IBrand,
  IPatient,
  IPatientRecall,
  NextRecallDue,
  RecallTrack,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  toMoment,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { Moment } from 'moment-timezone';
import { MomentModule } from 'ngx-moment';
import { from, Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { RecallAppointmentComponent } from '../recall-appointment/recall-appointment.component';

export interface IPatientRecallSettingsDialogRequest {
  patient: WithRef<IPatient>;
  track: RecallTrack;
}

export interface IPatientRecallSettingsFormData {
  disabled: boolean;
  periodInMonths: number;
  periodIsExplicit: boolean;
  nextDue?: Moment;
  nextDueIsExplicit: boolean;
  lastCompletedAt?: Moment;
  lastRecallAppointment?: DocumentReference<IAppointment>;
  nextRecallAppointment?: DocumentReference<IAppointment>;
}

export interface IPatientRecallSettingsDialogResponse {
  recall: IPatientRecall;
  track: RecallTrack;
}

@Component({
  selector: 'pr-patient-recall-settings-dialog',
  templateUrl: './patient-recall-settings-dialog.component.html',
  styleUrls: ['./patient-recall-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  imports: [
    NgDialogModule,
    PipesModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MomentModule,
    MatTooltipModule,
    RecallAppointmentComponent,
    InformationBoxComponent,
  ],
})
export class PatientRecallSettingsDialogComponent {
  private _dialogRef = inject(
    MatDialogRef<
      PatientRecallSettingsDialogComponent,
      IPatientRecallSettingsDialogResponse
    >
  );
  private _currentRecall: IPatientRecall;
  formGroup = new TypedFormGroup<IPatientRecallSettingsFormData>({
    disabled: new TypedFormControl<boolean>(false),
    periodInMonths: new TypedFormControl<number>(12),
    periodIsExplicit: new TypedFormControl<boolean>(false),
    nextDue: new TypedFormControl<Moment | undefined>(),
    nextDueIsExplicit: new TypedFormControl<boolean>(false),
    lastCompletedAt: new TypedFormControl<Moment | undefined>(),
    nextRecallAppointment: new TypedFormControl<
      DocumentReference<IAppointment> | undefined
    >(undefined),
    lastRecallAppointment: new TypedFormControl<
      DocumentReference<IAppointment> | undefined
    >(undefined),
  });
  nextDueCtrl = new TypedFormControl<Moment | undefined>();
  periodCtrl = new TypedFormControl<number>(12);
  request = inject<IPatientRecallSettingsDialogRequest>(MAT_DIALOG_DATA);
  title: Signal<string>;
  brand: Signal<WithRef<IBrand> | undefined>;
  defaultPeriod: Signal<number>;
  isDisabled$: Observable<boolean>;

  constructor() {
    this._currentRecall = this._resolveCurrentRecall();
    this.brand = toSignal(
      from(Firestore.getDoc(Patient.brandRef(this.request.patient)))
    );
    this.title = computed(
      () =>
        `Edit ${Recalls.trackLabel(this.brand(), this.request.track)} Settings`
    );

    this.defaultPeriod = computed(
      () =>
        this.brand()?.settings[this._settingsField(this.request.track)]
          ?.periodInMonths ?? DEFAULT_RECALL_PERIOD_IN_MONTHS
    );

    this.isDisabled$ = validFormControlChanges$(
      this.formGroup.controls.disabled as TypedFormControl<boolean>
    ).pipe(startWith(this._currentRecall.isDisabled));

    validFormControlChanges$(this.nextDueCtrl)
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.setNextDue(value));
    validFormControlChanges$(this.periodCtrl)
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.setPeriod(value));

    this._setInitialValues(this._currentRecall);
  }

  setPeriod(periodInMonths: number): void {
    this.formGroup.patchValue({
      periodInMonths,
      periodIsExplicit: true,
    });
  }

  resetRecallPeriod(): void {
    const periodInMonths =
      this.brand()?.settings[this._settingsField(this.request.track)]
        ?.periodInMonths ?? DEFAULT_RECALL_PERIOD_IN_MONTHS;
    this.periodCtrl.setValue(periodInMonths);
    this.formGroup.patchValue({ periodInMonths, periodIsExplicit: false });
  }

  setNextDue(nextDue?: Moment): void {
    this.formGroup.patchValue({
      nextDue,
      nextDueIsExplicit: true,
    });
  }

  clearNextDue(): void {
    this.nextDueCtrl.setValue(undefined);
    this.formGroup.patchValue({ nextDue: undefined, nextDueIsExplicit: true });
  }

  async resetNextDue(): Promise<void> {
    const brandSettings = this.brand()?.settings;
    if (!brandSettings) {
      return;
    }
    const recallSettings =
      brandSettings[this._settingsField(this.request.track)];
    if (!recallSettings) {
      return;
    }
    const currentRecall = this._getPatientRecall();
    const response = await Recalls.buildPatientRecall(
      this.request.patient,
      recallSettings,
      this.request.track,
      { ...currentRecall, isDisabled: false },
      true,
      true
    );
    const nextDue = response.nextRecallDue.dueAt
      ? toMoment(response.nextRecallDue.dueAt)
      : undefined;
    const lastCompletedAt = response.lastCompletedAt
      ? toMoment(response.lastCompletedAt)
      : undefined;

    this.nextDueCtrl.setValue(nextDue);
    this.formGroup.patchValue({
      disabled: false,
      nextDueIsExplicit: false,
      nextDue,
      lastCompletedAt,
      lastRecallAppointment: response.lastRecallAppointment,
      nextRecallAppointment: response.nextRecallAppointment,
    });
  }

  disableRecalls(): void {
    this.formGroup.patchValue({
      disabled: true,
      nextDue: undefined,
    });
  }

  submit(): void {
    this._dialogRef.close({
      recall: this._getPatientRecall(),
      track: this.request.track,
    });
  }

  private _settingsField(track: RecallTrack): BrandRecallField {
    return track === RecallTrack.Primary ? 'recalls' : 'secondaryRecalls';
  }

  private _resolveCurrentRecall(): IPatientRecall {
    return (
      this.request.patient[Recalls.trackToField(this.request.track)] ?? {
        isDisabled: false,
        periodInMonths: DEFAULT_RECALL_PERIOD_IN_MONTHS,
        periodIsExplicit: false,
        nextRecallDue: {
          isSet: false,
          isExplicit: false,
          dueAt: undefined,
        },
      }
    );
  }

  private _setInitialValues(recall: IPatientRecall): void {
    const nextDue = recall.nextRecallDue.dueAt
      ? toMoment(recall.nextRecallDue.dueAt)
      : undefined;
    const lastCompletedAt = recall?.lastCompletedAt
      ? toMoment(recall.lastCompletedAt)
      : undefined;
    this.formGroup.patchValue(
      {
        disabled: recall.isDisabled,
        periodInMonths: recall.periodInMonths,
        periodIsExplicit: recall.periodIsExplicit,
        nextDueIsExplicit: recall.nextRecallDue.isExplicit,
        nextDue,
        lastCompletedAt,
        lastRecallAppointment: recall?.lastRecallAppointment,
        nextRecallAppointment: recall?.nextRecallAppointment,
      },
      { emitEvent: true }
    );
    this.nextDueCtrl.setValue(nextDue);
    this.periodCtrl.setValue(recall.periodInMonths);
  }

  private _getPatientRecall(): IPatientRecall {
    const formData = this.formGroup.value;
    const nextRecallDue: NextRecallDue = formData.nextDue
      ? {
          isSet: true,
          isExplicit: formData.nextDueIsExplicit,
          dueAt: toTimestamp(formData.nextDue),
        }
      : {
          isSet: false,
          isExplicit: formData.nextDueIsExplicit,
          dueAt: undefined,
        };
    return {
      ...this._currentRecall,
      isDisabled: formData.disabled,
      periodInMonths: formData.periodInMonths,
      periodIsExplicit: formData.periodIsExplicit,
      nextRecallDue,
      lastCompletedAt: formData.lastCompletedAt
        ? toTimestamp(formData.lastCompletedAt)
        : undefined,
      lastRecallAppointment: formData.lastRecallAppointment,
      nextRecallAppointment: formData.nextRecallAppointment,
    };
  }
}
